import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { TokenStorageService } from '../../../services/token-storage.service';
import { LoggerService } from '../../../services/logger.service';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCircleCheck, lucideDownload, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmCheckboxImports } from '@spartan-ng/helm/checkbox';
import { HlmNativeSelectImports } from '@spartan-ng/helm/native-select';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { formatCurrencyWithSymbol } from '../../../utils/currency.utils';

@Component({
  selector: 'app-invoicing',
  imports: [
    FormsModule,
    RouterLink,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmCheckboxImports,
    HlmNativeSelectImports,
    HlmSpinnerImports,
    HlmTableImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({
      lucideCircleCheck,
      lucideDownload,
      lucideTriangleAlert,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './invoicing.component.html',
})
export class InvoicingComponent implements OnInit {
  readonly money = formatCurrencyWithSymbol;
  private apiUrl = environment.apiUrl;

  selectedYear = new Date().getFullYear();
  years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 3 + i);
  selectedMonths: number[] = [];
  downloadError: string | null = null;
  downloadSuccess: string | null = null;
  isDownloading = false;
  months = [
    { value: 1, name: 'Januar' },
    { value: 2, name: 'Februar' },
    { value: 3, name: 'März' },
    { value: 4, name: 'April' },
    { value: 5, name: 'Mai' },
    { value: 6, name: 'Juni' },
    { value: 7, name: 'Juli' },
    { value: 8, name: 'August' },
    { value: 9, name: 'September' },
    { value: 10, name: 'Oktober' },
    { value: 11, name: 'November' },
    { value: 12, name: 'Dezember' },
  ];

  fetchData: MonthlyFees[] = [];

  constructor(
    private http: HttpClient,
    private tokenStorage: TokenStorageService,
    private logger: LoggerService,
  ) {}
  async ngOnInit() {
    await this.fetchFees();
  }

  async fetchFees() {
    try {
      const token = this.tokenStorage.getAccessToken();
      const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
      this.fetchData = await firstValueFrom(
        this.http.get<MonthlyFees[]>(`${this.apiUrl}/fees/monthly/${this.selectedYear}`, {
          headers,
        }),
      );
    } catch (error) {
      this.logger.error('Error fetching invoice data:', error);
    }
  }

  getMonthlyFees(month: number): MonthlyFees | undefined {
    return this.fetchData.find((fee) => fee.month === month);
  }

  /** A month can only be picked when it is in the past and actually has fees. */
  isSelectable(month: number): boolean {
    return this.isMonthAvailable(month) && this.hasMonthlyFees(month);
  }

  toggleMonth(monthValue: number) {
    const index = this.selectedMonths.indexOf(monthValue);
    if (index === -1) {
      this.selectedMonths.push(monthValue);
    } else {
      this.selectedMonths.splice(index, 1);
    }
  }

  async downloadSelectedInvoices() {
    this.downloadError = null;
    this.downloadSuccess = null;
    this.isDownloading = true;

    // Check if any selected months have no fees
    const monthsWithoutFees = this.selectedMonths.filter((month) => !this.hasMonthlyFees(month));

    if (monthsWithoutFees.length > 0) {
      const monthNames = monthsWithoutFees
        .map((month) => this.months.find((m) => m.value === month)?.name)
        .join(', ');

      this.downloadError = `Für folgende Monate können keine Rechnungen heruntergeladen werden, da keine Transaktionen vorhanden sind: ${monthNames}`;
      this.isDownloading = false;
      return;
    }

    const token = this.tokenStorage.getAccessToken();
    let downloadCount = 0;

    this.selectedMonths.forEach((month) => {
      const url = `${this.apiUrl}/fees/invoice/${this.selectedYear}/${month}`;

      // Create a hidden anchor element
      const link = document.createElement('a');
      link.href = url;

      // Add the authorization header to the link
      fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then(async (response) => {
          // Check if the response is an error
          if (!response.ok) {
            const errorText = await response.text();
            let errorData;
            try {
              errorData = JSON.parse(errorText);
            } catch {
              throw new Error('Fehler beim Herunterladen der Rechnung');
            }
            throw new Error(errorData.detail || 'Fehler beim Herunterladen der Rechnung');
          }
          return response.blob();
        })
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = `schenkwerk-rechnung-${this.selectedYear}-${month}.pdf`; // Assuming PDF format
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          downloadCount++;

          if (downloadCount === this.selectedMonths.length) {
            this.downloadSuccess = `${downloadCount} Rechnung(en) erfolgreich heruntergeladen.`;
            this.selectedMonths = [];
            this.isDownloading = false;
          }
        })
        .catch((error) => {
          this.logger.error('Error downloading invoice:', error);
          this.downloadError =
            error.message ||
            'Fehler beim Herunterladen der Rechnung(en). Bitte versuchen Sie es erneut.';
          this.isDownloading = false;
        });
    });
  }

  async onYearChange() {
    await this.fetchFees();
    this.selectedMonths = [];
  }

  isMonthAvailable(month: number): boolean {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11

    if (this.selectedYear < currentYear) {
      return true; // All months available for past years
    } else if (this.selectedYear === currentYear) {
      return month < currentMonth; // Only past months available for current year
    }
    return false; // No months available for future years
  }

  /**
   * A month is invoiceable only if it actually carries billable fees. A month that
   * contains nothing but fee-free internal vouchers still returns a row (so the count
   * stays visible), but there is nothing to invoice, so it must not be selectable.
   */
  hasMonthlyFees(month: number): boolean {
    const fees = this.getMonthlyFees(month);
    return !!fees && fees.total_fees > 0;
  }

  /** Month has vouchers, but only fee-free internal ones. */
  hasOnlyInternalVouchers(month: number): boolean {
    const fees = this.getMonthlyFees(month);
    return !!fees && fees.total_fees <= 0 && (fees.internal_voucher_count ?? 0) > 0;
  }

  canDownloadSelectedMonths(): boolean {
    return (
      this.selectedMonths.length > 0 &&
      this.selectedMonths.every((month) => this.hasMonthlyFees(month))
    );
  }
}

export interface MonthlyFees {
  total_fees: number;
  total_amount: number;
  month: number;
  year: number;
  company_fees?: number;
  customer_fees_covered?: number;
  vouchers_with_covered_fee?: number;
  // Internal (raffle/giveaway) vouchers: fee-free, so never part of an invoice
  internal_voucher_count?: number;
  internal_voucher_value?: number;
}
